import WindowUrlService from "../window-url-service.js";
import { FILTER_IDS } from "../constants.js";
import GeolocationService from "./geolocation-service.js";
import ApiUtils from "../../../js/api-utils.es6.js";
import MyChartUser from '@js/mychart-user.es6.js';

export default class SearchApiService {
  constructor() {
    this.windowUrlService = new WindowUrlService();
    this.geolocationService = new GeolocationService();
    this.baseUrl = this.windowUrlService.getWindowOriginUrlWithLanguage();
    this.searchAllEndpoint = `${this.baseUrl}/search-all/rest`;
    this.searchLocationsEndpoint = `${this.baseUrl}/search-location/rest`;
    this.searchProvidersEndpoint = `${this.baseUrl}/search-provider/rest`;
    this.searchProviderSpecialtiesEndpoint = `${this.baseUrl}/provider-search/specialties`;
    this.blogCategoriesEndpoint = `${this.baseUrl}/blog-categories/rest`;
  }

  async getAllSearchResults(searchedText) {
    let searchEndpoint = `${this.searchAllEndpoint}`;
    if (searchedText) {
      searchEndpoint += `?search=${searchedText}`;
    }

    return await fetch(searchEndpoint)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async getSearchWithFilter(searchedText, pageCategory, blogCategory, pageNum) {
    // Set fields to empty in case they're null
    searchedText = searchedText ? searchedText : "";
    pageCategory = pageCategory ? pageCategory : "";
    blogCategory = blogCategory ? blogCategory : "";
    switch (pageCategory) {
      case FILTER_IDS.CONDITION:
        return await this.searchConditionPages(searchedText, pageNum);
      case FILTER_IDS.LEADERSHIP:
        return await this.searchLeadership(searchedText, pageNum);
      case FILTER_IDS.LOCATION:
        return await this.searchLuminisLocations(searchedText, pageNum);
      case FILTER_IDS.PROVIDER:
        return await this.searchProviders({ searchedText, pageNum });
      case FILTER_IDS.RESEARCH:
        return await this.searchResearchPages(searchedText, pageNum);
      case FILTER_IDS.SERVICE:
        return await this.searchServicePages(searchedText, pageNum);
      case FILTER_IDS.SPECIALTY:
        return await this.searchSpecialtyPages(searchedText, pageNum);
      case FILTER_IDS.TREATMENT:
        return await this.searchTreatmentPages(searchedText, pageNum);
      case FILTER_IDS.BLOG:
        return await this.searchBlogPages(searchedText, pageNum, blogCategory);
      default:
        return await this.searchAllWithPageNum(searchedText, pageNum, blogCategory);
    }
  }

  async searchSpecialtyPages(searchedText, pageNum) {
    return await fetch(`${this.baseUrl}/search-specialty/rest?search=${searchedText}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchResearchPages(searchedText, pageNum) {
    return await fetch(`${this.searchAllEndpoint}?search=${searchedText}&type=${FILTER_IDS.RESEARCH}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchConditionServiceTreatmentPages(searchedText, pageNum) {
    return await fetch(`${this.searchAllEndpoint}?search=${searchedText}&type=cond_serv_treatments_page&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchConditionPages(searchedText, pageNum) {
    return await fetch(`${this.searchAllEndpoint}?search=${searchedText}&type=cond_serv_treatments_page&category=${FILTER_IDS.CONDITION}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchServicePages(searchedText, pageNum) {
    return await fetch(`${this.searchAllEndpoint}?search=${searchedText}&type=cond_serv_treatments_page&category=${FILTER_IDS.SERVICE}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchTreatmentPages(searchedText, pageNum) {
    return await fetch(`${this.searchAllEndpoint}?search=${searchedText}&type=cond_serv_treatments_page&category=${FILTER_IDS.TREATMENT}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchBlogPages(searchedText, pageNum, blogCategory="") {
    return await fetch(`${this.searchAllEndpoint}?search=${searchedText}&general_page_category=Blog&blog_category=${blogCategory}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchBlogCategories(searchedText) {
    let queryParams = {search: searchedText};
    return await this.#getAllSearchApiResults(this.blogCategoriesEndpoint, queryParams);
  }

  async searchAllWithPageNum(searchedText, pageNum, blogCategory="") {
    return await fetch(`${this.searchAllEndpoint}?search=${searchedText}&blog_category=${blogCategory}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchContentWithNoProvidersOrLocations(searchedText, pageNum) {
    return await fetch(`${this.baseUrl}/search-content/rest?search=${searchedText}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchContentWithNoProvidersOrLocationsByTitle(searchedText, pageNum = 0) {
    return await fetch(`${this.baseUrl}/search-content/rest?search=&title=${searchedText}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  /**
   * Search provider content based on location, name, specialty, and whether or not the provider is configured for online scheduling.
   *
   * @param {object} params
   * @param {string} params.searchedText the general search term
   * @param {object} params.userLocation the user's location
   * @param {string} params.name the provider's name
   * @param {string} params.specialty the provider's specialty
   * @param {boolean} params.configuredForOnlineScheduling true, if you only want to return providers who are configured for online scheduling (false is
   * ignored and does not alter the search)
   * @param {number} params.pageNum the page number of the search to return (all pages are fetched if not provided)
   * @return {object} all matching providers
   */
  async searchProviders({ searchedText = '', userLocation, name, specialty, configuredForOnlineScheduling = false, pageNum }) {
    searchedText = searchedText.toLowerCase();

    let path = 'search-provider/';
    if (configuredForOnlineScheduling === true) {
      const myChartUser = await MyChartUser.getLoggedInUser();
      if (myChartUser) {
        path += 'online-scheduling/authenticated';
      }
      else {
        path += 'online-scheduling/unauthenticated';
      }
    }
    else {
      path += 'rest';
    }

    const queryParams = {
      search: searchedText,
      type: FILTER_IDS.PROVIDER,
    };

    if (userLocation) {
      queryParams['geolocation[distance][from]'] = 1000;

      let userLatLon;
      if (typeof userLocation === 'string') {
        userLatLon = await this.convertStringLocationToLatLong(userLocation);
      } else {
        userLatLon = userLocation;
      }
      queryParams['geolocation[value]'] = `${userLatLon.lat},${userLatLon.lng}`;
    }

    if (name) {
      queryParams['title'] = searchedText
    }

    if (specialty) {
      queryParams['specialty'] = `'${specialty.toLowerCase()}'`; // The quotes around the specialty are very important here to allow searching on specialties with "and" in them
    }

    if (pageNum || pageNum == 0) {
      return await this.#getSinglePageSearchApiResults(path, pageNum, queryParams);
    }
    else {
      return await this.#getAllSearchApiResults(path, queryParams);
    }
  }

  /**
   * Search provider specialties.
   *
   * @param {object} params
   * @param {string} params.searchedText the general search term
   * @param {number} params.pageNum the page number of the search to return (all pages are fetched if not provided)
   * @return {object} all matching provider specialties
   */
  async searchProviderSpecialties({ searchedText, pageNum }) {
    const path = this.searchProviderSpecialtiesEndpoint;
    const queryParams = {
      search: searchedText.toLowerCase()
    };

    if (pageNum || pageNum == 0) {
      return await this.#getSinglePageSearchApiResults(path, pageNum, queryParams);
    }
    else {
      return await this.#getAllSearchApiResults(path, queryParams);
    }
  }

  async searchLeadership(searchedText, pageNum) {
    return await fetch(`${this.searchAllEndpoint}?search=${searchedText}&type=${FILTER_IDS.LEADERSHIP}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchLuminisLocations(searchedText, pageNum) {
    return await fetch(`${this.searchLocationsEndpoint}?search=${searchedText}&is_luminis_location=1&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchAllLocations(searchedText, pageNum) {
    return await fetch(`${this.searchLocationsEndpoint}?search=${searchedText}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchProvidersByName(searchedText) {
    return await fetch(`${this.searchProvidersEndpoint}?search=${searchedText}&title=${searchedText}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchProvidersWithDistance(searchedText, userLocation, pageNum, specialty) {
    let userLatLon;
    let fetchString;

    if (typeof userLocation === 'string') {
      userLatLon = await this.convertStringLocationToLatLong(userLocation);
      fetchString = `${this.searchProvidersEndpoint}?search=${searchedText.toLowerCase()}&geolocation[distance][from]=1000&geolocation[value]=${userLatLon.lat},${userLatLon.lng}&page=${pageNum}`
    } else {
      userLatLon = userLocation;
      fetchString = `${this.searchProvidersEndpoint}?search=${searchedText.toLowerCase()}&geolocation[distance][from]=1000&geolocation[value]=${userLatLon.lat},${userLatLon.lng}&page=${pageNum}`
    }

    if (specialty) {
      fetchString += `&specialty="${specialty}"`;
    }

    return await fetch(fetchString)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async convertStringLocationToLatLong(userLocation) {
    const userLatLong = await this.geolocationService.getLatLonFromAddress(userLocation);

    return {
      'lat': `${userLatLong.lat()}`,
      'lng': `${userLatLong.lng()}`
    };
  }

  async searchLocationByName(searchedText, pageNum) {
    return await fetch(`${this.searchLocationsEndpoint}?search=${searchedText}&name=${searchedText}&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchLuminisLocationByName(searchedText, pageNum) {
    return await fetch(`${this.searchLocationsEndpoint}?search=${searchedText}&name=${searchedText}&is_luminis_location=1&page=${pageNum}`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchLocationByEpicId(epicId) {
    return await fetch(`${this.searchLocationsEndpoint}?search=&name=&epic_id=${this.setEpicId(epicId)}&page=0`)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchLocationByNid(nid) {
    // moved to api utils for re-use but leaving this to ease refactoring.
    return await ApiUtils.searchLocationByNid(nid);
  }

  async searchLuminisLocationsWithDistance(searchedText, userLocation, pageNum, epicId, nid = '') {
    let userLatLon;
    let fetchString;

    if (typeof userLocation === 'string') {
      userLatLon = await this.geolocationService.getLatLonFromAddress(userLocation);
      fetchString = `${this.searchLocationsEndpoint}?search=${searchedText}&geolocation[distance][from]=1000&geolocation[value]=${userLatLon.lat()},${userLatLon.lng()}&epic_id=${this.setEpicId(epicId)}&nid=${nid}&is_luminis_location=1&page=${pageNum}`
    } else {
      userLatLon = userLocation;
      fetchString = `${this.searchLocationsEndpoint}?search=${searchedText}&geolocation[distance][from]=1000&geolocation[value]=${userLatLon.lat},${userLatLon.lng}&epic_id=${this.setEpicId(epicId)}&nid=${nid}&is_luminis_location=1&page=${pageNum}`
    }
    return await fetch(fetchString)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  async searchAllLocationsWithDistance(searchedText, userLocation, pageNum, epicId, nid = '') {
    let userLatLon;
    let fetchString;

    if (typeof userLocation === 'string') {
      userLatLon = await this.geolocationService.getLatLonFromAddress(userLocation);
      fetchString = `${this.searchLocationsEndpoint}?search=${searchedText}&geolocation[distance][from]=1000&geolocation[value]=${userLatLon.lat()},${userLatLon.lng()}&epic_id=${this.setEpicId(epicId)}&nid=${nid}&page=${pageNum}`
    } else {
      userLatLon = userLocation;
      fetchString = `${this.searchLocationsEndpoint}?search=${searchedText}&geolocation[distance][from]=1000&geolocation[value]=${userLatLon.lat},${userLatLon.lng}&epic_id=${this.setEpicId(epicId)}&nid=${nid}&page=${pageNum}`
    }
    return await fetch(fetchString)
      .then(res => res.json())
      .then(
        (results) => {
          return results
        },
        (error) => {
          console.error(error);
        }
      )
  }

  convertBooleanStringToRealBoolean(boolString) {
    return boolString === "1";
  }

  setEpicId(epicId) {
    // Default value for Epic ID in the Locations Search API is "all".
    if (epicId && epicId.length > 0) {
      return epicId;
    } return 'all';
  }

  async #getSinglePageSearchApiResults(path, pageNum, queryParams = {}) {
    let baseUrl = this.baseUrl;
    baseUrl += baseUrl.endsWith('/') ? '' : '/';

    const url = new URL(path, baseUrl);
    for (const queryParam in queryParams) {
      url.searchParams.set(queryParam, queryParams[queryParam]);
    }
    url.searchParams.set('page', pageNum);

    return await fetch(url.toString())
      .then(res => res.json())
      .then(
        (results) => {
          return results;
        },
        (error) => {
          console.error(error);
        }
      );
  };

  async #getAllSearchApiResults(path, queryParams = {}) {
    const firstPageResult = await this.#getSinglePageSearchApiResults(path, 0, queryParams);
    const { total_pages: totalPages } = firstPageResult.pager;

    const promises = [];
    for (let i = 1; i < totalPages; i++) {
      promises[i - 1] = this.#getSinglePageSearchApiResults(path, i, queryParams);
    }

    const resultedPromises = await Promise.allSettled(promises);
    const results = resultedPromises.filter(r => r.value).map(r => r.value);
    results.splice(0, 0, firstPageResult);
    return results;
  };
}
